"use client";

import { useEffect, useRef, useState } from "react";
import Turnstile from "react-turnstile";
import { validateEmail, validateRequired, validateMaxLength, validateCaptchaToken, validateCheckbox } from "@/lib/validation-helpers";
import { getCookie, setCookie } from "@/utils/cookieUtils";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { PrivacyInfoBlock } from "@/components/ui/PrivacyInfoBlock";
import {
  ERROR_TEXT_CLASS,
  FIELD_WRAPPER_CLASS,
  renderRequiredLabel,
  getInputClassesWithError,
} from "@/components/ui/formClasses";

type ChaveAcessosSimulFormLocalizedStrings = {
  pageTitle: string;
  pageIntro?: string;
  requiredFieldsLabel: string;
  labelNome: string;
  placeholderNome: string;
  labelEmail: string;
  placeholderEmail: string;
  labelEmpresa: string;
  placeholderEmpresa: string;
  labelSetor: string;
  placeholderSetor: string;
  labelNumSerial: string;
  placeholderNumSerial: string;
  labelObservacao: string;
  placeholderObservacao: string;
  labelConsentimento: string;
  submitButton: string;
  submittingButton: string;
  submitedButton: string;
  processingButton: string;
  errorNomeRequired: string;
  errorEmailRequired: string;
  errorEmailInvalid: string;
  errorEmpresaRequired: string;
  errorSetorRequired: string;
  errorNumSerialRequired: string;
  errorNumSerialMaxLength: string;
  errorConsentimentoRequired: string;
  errorCaptchaRequired: string;
  errorCaptchaFailed: string;
  errorConnection: string;
  errorFormSubmission: string;
  captchaWarning: string;
  successRequest: string;
  successEmail: string;
  successApprovers: string;
  successIdLabel: string;
};

type ChaveAcessosSimulFormProps = {
  locale: string;
  title: string;
  intro?: string;
  initialNumSerial?: string | undefined;
  localizedStrings: ChaveAcessosSimulFormLocalizedStrings;
};

// Render the public request form for simultaneous access keys.
export function ChaveAcessosSimulForm({ locale, title, intro, initialNumSerial, localizedStrings }: ChaveAcessosSimulFormProps) {
  const turnstileRef = useRef<string>("");
  const [cookiesLoaded, setCookiesLoaded] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    empresa: "",
    setor: "",
    numSerial: "",
    observacao: "",
    consentimento: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const submittingRef = useRef(false);

  // Load persisted fields from cookies when the locale changes or the page first mounts.
  useEffect(() => {
    setFormData({
      nome: getCookie("form_chave_acessos_simul_nome") || "",
      email: getCookie("form_chave_acessos_simul_email") || "",
      empresa: getCookie("form_chave_acessos_simul_empresa") || "",
      setor: getCookie("form_chave_acessos_simul_setor") || "",
      numSerial: initialNumSerial ||getCookie("form_chave_acessos_simul_numSerial") || "",
      observacao: getCookie("form_chave_acessos_simul_observacao") || "",
      consentimento: false,
    });
    setCookiesLoaded(true);
  }, [locale , initialNumSerial]);

  // Persist the editable fields so the user can resume the form later.
  useEffect(() => {
    if (!cookiesLoaded) {
      return;
    }

    setCookie("form_chave_acessos_simul_nome", formData.nome);
    setCookie("form_chave_acessos_simul_email", formData.email);
    setCookie("form_chave_acessos_simul_empresa", formData.empresa);
    setCookie("form_chave_acessos_simul_setor", formData.setor);
    setCookie("form_chave_acessos_simul_numSerial", formData.numSerial);
    setCookie("form_chave_acessos_simul_observacao", formData.observacao);
    // consentimento intentionally NOT saved to cookies
  }, [formData, cookiesLoaded]);

  // Update the form state when the user edits a field.
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, type, value, checked } = e.target as any;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  // Validate the form before sending it to the backend.
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!validateRequired(formData.nome)) {
      newErrors.nome = localizedStrings.errorNomeRequired;
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = localizedStrings.errorEmailRequired;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = localizedStrings.errorEmailInvalid;
    }

    if (!validateRequired(formData.empresa)) {
      newErrors.empresa = localizedStrings.errorEmpresaRequired;
    }

    if (!validateRequired(formData.setor)) {
      newErrors.setor = localizedStrings.errorSetorRequired;
    }

    if (!validateRequired(formData.numSerial)) {
      newErrors.numSerial = localizedStrings.errorNumSerialRequired;
    } else if (!validateMaxLength(formData.numSerial, 40)) {
      newErrors.numSerial = localizedStrings.errorNumSerialMaxLength;
    }

    if (process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" && !validateCaptchaToken(turnstileRef.current)) {
      newErrors.turnstile = localizedStrings.errorCaptchaRequired;
    }

    if (!validateCheckbox((formData as any).consentimento)) {
      newErrors.consentimento = localizedStrings.errorConsentimentoRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit the request to the API.
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" && !turnstileRef.current) {
      setErrors((prev) => ({ ...prev, turnstile: localizedStrings.errorCaptchaRequired }));
      return;
    }

    if (submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/formularios/chave-acessos-simul", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          idioma: locale,
          turnstileToken: turnstileRef.current,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // submission failed — re-enable the form so the user can try again
        setSubmitStatus("error");
        setSubmitMessage(data.message || localizedStrings.errorFormSubmission);
        setIsSubmitting(false);
        submittingRef.current = false;
        return;
      }

      // success — keep the form disabled (prevent further submits)
      setSubmitStatus("success");
      const idSolic = data.idSolic;
      const messages: string[] = [
        `${localizedStrings.successRequest} ${idSolic}`,
      ];

      if (data.emailSentToRequester) {
        messages.push(localizedStrings.successEmail);
      }

      if (data.emailSentToApprovers) {
        messages.push(localizedStrings.successApprovers);
      }

      setSubmitMessage(messages.join("\n"));
      turnstileRef.current = "";
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting chave acessos simultaneos form:", error);
      setSubmitStatus("error");
      setSubmitMessage(localizedStrings.errorConnection);
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  };

  const errorClasses = ERROR_TEXT_CLASS;
  const fieldWrapperClasses = FIELD_WRAPPER_CLASS;

  return (
    <section className="rounded-3xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(235,243,252,0.99))] p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">Sistech Sistemas</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-blue-950 sm:text-4xl">{title}</h1>
      {intro ? <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 sm:text-lg">{intro}</p> : null}

      <p className="mt-6 text-sm font-medium text-slate-700">
        <span className="text-red-600">*</span> {localizedStrings.requiredFieldsLabel.replace("* ", "")}
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        <div className={fieldWrapperClasses}>
          <label htmlFor="nome" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelNome)}
          </label>
          <input
            id="nome"
            name="nome"
            type="text"
            value={formData.nome}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.nome, isSubmitting)}
            placeholder={localizedStrings.placeholderNome}
          />
          {errors.nome && <p className={errorClasses}>{errors.nome}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="email" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelEmail)}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.email, isSubmitting)}
            placeholder={localizedStrings.placeholderEmail}
          />
          {errors.email && <p className={errorClasses}>{errors.email}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="empresa" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelEmpresa)}
          </label>
          <input
            id="empresa"
            name="empresa"
            type="text"
            value={formData.empresa}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.empresa, isSubmitting)}
            placeholder={localizedStrings.placeholderEmpresa}
          />
          {errors.empresa && <p className={errorClasses}>{errors.empresa}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="setor" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelSetor)}
          </label>
          <input
            id="setor"
            name="setor"
            type="text"
            value={formData.setor}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.setor, isSubmitting)}
            placeholder={localizedStrings.placeholderSetor}
          />
          {errors.setor && <p className={errorClasses}>{errors.setor}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="numSerial" className="text-sm font-medium text-blue-950">
            {renderRequiredLabel(localizedStrings.labelNumSerial)}
          </label>
          <input
            id="numSerial"
            name="numSerial"
            type="text"
            value={formData.numSerial}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.numSerial, isSubmitting)}
            placeholder={localizedStrings.placeholderNumSerial}
          />
          {errors.numSerial && <p className={errorClasses}>{errors.numSerial}</p>}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="observacao" className="text-sm font-medium text-blue-950">
            {localizedStrings.labelObservacao}
          </label>
          <textarea
            id="observacao"
            name="observacao"
            rows={5}
            value={formData.observacao}
            onChange={handleChange}
            disabled={isSubmitting}
            className={getInputClassesWithError(!!errors.observacao, isSubmitting)}
            placeholder={localizedStrings.placeholderObservacao}
          />
        </div>

        <div className={fieldWrapperClasses}>
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              name="consentimento"
              checked={(formData as any).consentimento}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`mt-1 h-5 w-5 rounded border-blue-900/15 text-blue-600 focus:ring-2 focus:ring-blue-300/60 ${
                errors.consentimento ? "border-red-500" : ""
              }`}
            />
            <span className="text-sm text-slate-700">
              {renderRequiredLabel(localizedStrings.labelConsentimento, 'start')}
            </span>
          </label>
          {errors.consentimento && <p className={errorClasses}>{errors.consentimento}</p>}
        </div>

        {process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" ? (
          <div className={fieldWrapperClasses}>
            <div className={`flex justify-center p-4 ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}>
              <Turnstile
                sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""}
                onSuccess={(token) => {
                  turnstileRef.current = token;
                  if (errors.turnstile) {
                    setErrors((prev) => {
                      const nextErrors = { ...prev };
                      delete nextErrors.turnstile;
                      return nextErrors;
                    });
                  }
                }}
                onExpire={() => {
                  turnstileRef.current = "";
                }}
              />
            </div>
            {errors.turnstile ? <p className={errorClasses}>{errors.turnstile}</p> : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-3 pt-6">
          <button
            type="submit"
            disabled={submitStatus === "success"}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                  <LoadingSpinner size="sm" ariaLabel="Enviando..." />
                  {localizedStrings.processingButton}
              </>
              ) : submitStatus === "success" ? (
                localizedStrings.submitedButton // "Enviado"
              ) : (
                localizedStrings.submitButton // "Enviar"
              )
            }
          </button>
        </div>

        {submitMessage ? (
          <div className={`rounded-2xl border px-4 py-3 text-sm whitespace-pre-line ${submitStatus === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-900"}`}>
            {submitMessage}
          </div>
        ) : null}

        {/* Privacy Information */}
        <PrivacyInfoBlock />
      </form>
    </section>
  );
}